import { useCallback, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import RatingStars from "@/components/base/RatingStars";
import SoftwareCard from "@/components/base/SoftwareCard";
import Toast from "@/components/base/Toast";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  formatDate,
  formatDownloads,
  getRelatedSoftware,
  getSoftwareById,
} from "@/utils/software";
import type { Software } from "@/utils/software";
import ScreenshotGallery from "./components/ScreenshotGallery";
import DownloadPanel from "./components/DownloadPanel";
import CommentSection from "./components/CommentSection";

const changelog = [
  { version: "当前版本", date: "2026-09", content: "优化行情推送速度，新增多屏工作区布局保存功能。" },
  { version: "上一版本", date: "2026-07", content: "新增 8 组技术指标模板，修复部分机型通知延迟问题。" },
  { version: "历史版本", date: "2026-05", content: "重构自选列表交互，提升大盘数据加载效率。" },
];

export default function Detail() {
  const { softwareId = "" } = useParams();
  const navigate = useNavigate();
  const item = getSoftwareById(softwareId);
  const [toast, setToast] = useState<string | null>(null);

  useDocumentMeta({
    title: item
      ? `${item.name}免费下载及介绍 - 黄金网站软件app免费`
      : "软件详情 - 黄金网站软件app免费",
    description: item
      ? `${item.name}免费下载：${item.intro}支持${item.platforms.join("、")}，当前版本 v${item.version}，帮助黄金投资者高效掌握行情与理财工具。`
      : "黄金投资软件详情页。",
    keywords: item ? `${item.name},${item.name}下载,${item.tags.join(",")},黄金投资软件` : "黄金投资软件",
  });

  const handleDownload = useCallback((target: Software) => {
    setToast(`已打开「${target.name}」的示范下载链接（演示功能，无真实下载）`);
  }, []);

  if (!item) {
    return (
      <SiteLayout>
        <div className="container-page py-16">
          <EmptyState
            icon="ri-error-warning-line"
            title="页面不存在"
            desc="你访问的软件信息暂时无法找到，可能已下架或链接有误。"
            actionLabel="返回全部软件"
            onAction={() => navigate("/softwares")}
          />
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedSoftware(item, 3);
  const specs = [
    { label: "软件分类", value: item.categoryName },
    { label: "细分功能", value: item.subCategory },
    { label: "当前版本", value: `v${item.version}` },
    { label: "文件大小", value: item.size },
    { label: "支持平台", value: item.platforms.join(" / ") },
    { label: "更新时间", value: formatDate(item.updatedAt) },
    { label: "累计下载", value: `${formatDownloads(item.downloads)}次` },
    { label: "授权方式", value: "免费使用" },
  ];

  return (
    <SiteLayout>
      <div className="container-page py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: item.categoryName, to: `/category/${item.categoryId}` },
            { label: item.name },
          ]}
        />

        <section className="mt-6 overflow-hidden rounded-lg bg-secondary-950">
          <div className="grid gap-0 md:grid-cols-[1.1fr_1fr]">
            <div className="relative h-[220px] w-full bg-secondary-900 md:h-full md:min-h-[300px]">
              <img
                src={item.cover}
                alt={`${item.name}软件主图`}
                title={`${item.name} 黄金投资软件`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="flex flex-col gap-4 p-6 md:p-8">
              <span className="chip w-fit bg-primary-500/15 text-primary-200">
                <i className="ri-price-tag-3-line" />
                {item.categoryName} · {item.subCategory}
              </span>
              <div>
                <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
                  {item.name}
                </h1>
                <p className="mt-2 text-sm leading-relaxed text-background-300">{item.intro}</p>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-300">
                <span className="flex items-center gap-1">
                  <i className="ri-price-tag-3-line text-primary-300" />v{item.version}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-hard-drive-2-line text-primary-300" />
                  {item.size}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-download-2-line text-primary-300" />
                  {formatDownloads(item.downloads)}次下载
                </span>
                <span className="flex items-center gap-1">
                  <RatingStars rating={item.rating} />
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <span key={tag} className="chip border border-secondary-700 text-background-200">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button type="button" onClick={() => handleDownload(item)} className="btn-accent">
                  <i className="ri-download-2-line" />免费下载
                </button>
                <span className="text-xs text-background-400">
                  更新于 {formatDate(item.updatedAt)}
                </span>
              </div>
            </div>
          </div>
        </section>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px] lg:items-start">
          <div className="order-2 flex flex-col gap-8 lg:order-1">
            <ScreenshotGallery images={item.screenshots} name={item.name} />

            <section>
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
                <i className="ri-file-text-line text-primary-600" />
                软件介绍
              </h2>
              <p className="text-sm leading-relaxed text-foreground-700">{item.description}</p>
            </section>

            <section>
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
                <i className="ri-star-smile-line text-primary-600" />
                功能亮点
              </h2>
              <ul className="grid gap-3 sm:grid-cols-2">
                {item.features.map((feature) => (
                  <li
                    key={feature}
                    className="card-surface flex items-start gap-2 p-4 text-sm text-foreground-700"
                  >
                    <i className="ri-check-line mt-0.5 text-lg text-primary-600" />
                    {feature}
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
                <i className="ri-list-settings-line text-primary-600" />
                软件规格
              </h2>
              <dl className="card-surface grid grid-cols-1 gap-px overflow-hidden bg-background-200 sm:grid-cols-2">
                {specs.map((spec) => (
                  <div key={spec.label} className="flex items-center justify-between gap-3 bg-background-50 px-4 py-3">
                    <dt className="text-sm text-foreground-500">{spec.label}</dt>
                    <dd className="text-sm font-medium text-foreground-900">{spec.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section>
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
                <i className="ri-history-line text-primary-600" />
                更新日志
              </h2>
              <ol className="card-surface flex flex-col divide-y divide-background-200">
                {changelog.map((log) => (
                  <li key={log.date} className="flex flex-col gap-1 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="chip bg-secondary-100 text-secondary-800">{log.version}</span>
                      <span className="text-xs text-foreground-400">{log.date}</span>
                    </div>
                    <p className="text-sm text-foreground-700">{log.content}</p>
                  </li>
                ))}
              </ol>
            </section>

            <CommentSection softwareName={item.name} />
          </div>

          <div className="order-1 flex flex-col gap-4 lg:order-2 lg:sticky lg:top-28">
            <DownloadPanel item={item} onDownload={handleDownload} />
          </div>
        </div>

        <section className="mt-10">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
            <i className="ri-magic-line text-primary-600" />
            相关推荐
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-product-shop>
            {related.map((rel) => (
              <SoftwareCard key={rel.id} item={rel} onDownload={handleDownload} />
            ))}
          </div>
        </section>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-6">
          <Link to="/softwares" className="btn-ghost">
            <i className="ri-arrow-left-line" />
            返回软件列表
          </Link>
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="btn-primary"
          >
            <i className="ri-arrow-up-line" />
            返回顶部
          </button>
        </div>
      </div>
      <Toast message={toast} onClose={() => setToast(null)} />
    </SiteLayout>
  );
}